import { useEffect, useMemo, useState } from "react";
import type { FieldPath, FieldValues } from "react-hook-form";

import { getCountries } from "@/shared/services/countries";
import { normalizeCountryLabel } from "@/shared/utils/country";

import type { BaseInputProps, InputOption } from "@/shared/types";
import { SelectInput } from "./SelectInput";

type CountryOption = InputOption<string> & {
  id?: number;
};

const AUTO_UAE_FROM_COVERAGES = new Set([
  "Worldwide",
  "Schengen",
  "GCC Countries",
  "Hajj and Umrah",
]);

type CountrySelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = BaseInputProps<TFieldValues, TName> & {
  coverage?: string;
  excludeCountry?: string;
  options?: CountryOption[];
  searchable?: boolean;
  hideChevron?: boolean;
};

export function CountrySelect<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  placeholder = "Select country",
  description,
  disabled,
  required,
  className,
  coverage,
  excludeCountry,
  options: providedOptions,
  searchable = true,
  hideChevron = false,
}: CountrySelectProps<TFieldValues, TName>) {
  const [options, setOptions] = useState<CountryOption[]>([]);

  useEffect(() => {
    if (providedOptions !== undefined) {
      setOptions(providedOptions);
      return;
    }

    let active = true;

    async function loadCountries() {
      try {
        let coverageId: number | undefined;
        const storedCoverages = localStorage.getItem("coverageOptionsWithIds");

        if (coverage && storedCoverages) {
          const coverageList = JSON.parse(storedCoverages) as CountryOption[];
          const matchedCoverage = coverageList.find(
            (item) => item.value === coverage || item.label === coverage
          );
          coverageId = matchedCoverage?.id;
        }

        const countryResponse = await getCountries(coverageId ? { id: coverageId } : {});
        const fetchedOptions = (Array.isArray(countryResponse) ? countryResponse : []).map(
          (country: CountryOption) => ({
            value: normalizeCountryLabel(country.value),
            label: normalizeCountryLabel(country.label),
          })
        );

        if (active) {
          setOptions(fetchedOptions);
        }
      } catch (error) {
        console.error("Failed to load countries", error);

        if (active) {
          setOptions([]);
        }
      }
    }

    void loadCountries();

    return () => {
      active = false;
    };
  }, [coverage, providedOptions]);

  const filteredOptions = useMemo(() => {
    let nextOptions = options;

    if (excludeCountry) {
      const excluded = normalizeCountryLabel(excludeCountry);
      nextOptions = nextOptions.filter((option) => option.value !== excluded);
    }

    if (name === "destination" && coverage && AUTO_UAE_FROM_COVERAGES.has(coverage)) {
      nextOptions = nextOptions.filter((option) => option.value !== "United Arab Emirates");
    }

    if (
      name === "fromCountry" &&
      coverage &&
      AUTO_UAE_FROM_COVERAGES.has(coverage) &&
      !nextOptions.some((option) => option.value === "United Arab Emirates")
    ) {
      nextOptions = [
        {
          value: "United Arab Emirates",
          label: "United Arab Emirates",
        },
        ...nextOptions,
      ];
    }

    if (
      name === "destination" &&
      coverage === "UAE Inbound" &&
      !nextOptions.some((option) => option.value === "United Arab Emirates")
    ) {
      nextOptions = [
        {
          value: "United Arab Emirates",
          label: "United Arab Emirates",
        },
        ...nextOptions,
      ];
    }

    return nextOptions;
  }, [coverage, excludeCountry, name, options]);

  return (
    <SelectInput
      control={control}
      name={name}
      label={label}
      placeholder={placeholder}
      description={description}
      disabled={disabled}
      required={required}
      className={className}
      options={filteredOptions}
      searchable={searchable}
      hideChevron={hideChevron}
    />
  );
}
